import { Box, Typography, IconButton, Divider, CircularProgress, FormControl, Select, MenuItem, FormHelperText } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { AddButton } from "../../button/button";
import ExpertSearchBar from "./expertSearchBar";
import { AppDispatch, RootState } from "../../../redux/store/appStore";
import { useDispatch, useSelector } from "react-redux";
import { STATUS } from "../../../utils/status";
import debounce from "lodash.debounce";
import { getProjectExpertsDeboard, updateProject } from "../../../redux/slices/projectSlice/addProjectSlice";
import { useParams } from "react-router-dom";
import DeboardExpertSearchBar from "./deboardExpertSearchBar";

interface InviteEmailInterface {
    handleClose: () => void;
    alignment: string;
    selectedStage?: string;
}

const DeboardExpertDrawer: React.FC<InviteEmailInterface> = ({
    handleClose, alignment, selectedStage
}) => {
    const [selectedOption, setSelectedOption] = useState(selectedStage);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = useState<any[]>([]);
    const [loading] = useState(false);
    const [selectedExperts, setSelectedExperts] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const experts = useSelector((state: RootState) => state.projects.getProjectExpertsDeboard);
    const getStatus = useSelector((state: RootState) => state.projects.getProjectExpertsDeboardStatus);
    const updateStatus = useSelector((state: RootState) => state.projects.updateStatus);
    const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);
    const getReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();

    useEffect(() => {
        dispatch(getProjectExpertsDeboard({
            project_id: id,
            type: alignment === 'tasker' ? 'TASK' : 'REVIEW',
            paginate: true,
            search: searchTerm,
            stage_id: selectedOption,
            fields: JSON.stringify(['id', 'expert.id', 'expert.first_name', 'is_blocked', 'expert.last_name', 'expert.email', 'expert.pseudo_name', 'created_at', 'type', 'to_be_type', 'review_stage.name', 'task_limit', 'expert.user_name', 'role_id', 'role.name', 'stage_id','is_qualified']),
        }));
    }, [dispatch, searchTerm, id, alignment, selectedOption]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value.trim());
    };

    const debouncedResults = useMemo(() => { return debounce(handleSearchChange, 300); }, []);

    useEffect(() => {
        return () => {
            debouncedResults.cancel();
        };
    });

    const handleOptionChange = (event: any) => {
        setSelectedOption(event.target.value);
    };

    const handleAddAllClick = () => {
        const payload = {
            id: id,
            unassign: {
                [alignment === 'tasker' ? 'taskers' : 'reviewers']: selectedExperts.map(expert => (expert.expert_id)),
                stage_id: selectedOption
            },
        };
        dispatch(updateProject(payload))
    };

    const handleAutocompleteInput = (e: any) => {
        debouncedResults(e)
    };

    useEffect(() => {
        if (getStatus === STATUS.SUCESS) {
            setOptions(experts.results)
        }
    }, [getStatus])

    useEffect(() => {
        setSelectedExperts([])
    }, [selectedOption])

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                    sx={{
                        fontFamily: "Montserrat",
                        fontWeight: "600",
                        fontSize: "18px",
                        color: "#10383D",
                        marginBottom: "8px",
                    }}
                >
                    Deboard Experts
                </Typography>
                <IconButton onClick={handleClose} sx={{ padding: "4px" }}>
                    <CloseIcon sx={{ color: "#10383D" }} />
                </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <DeboardExpertSearchBar
                open={open}
                handleOpen={() => setOpen(true)}
                handleClose={() => setOpen(false)}
                options={options}
                selectedExperts={selectedExperts}
                setSelectedExperts={setSelectedExperts}
                loading={loading}
                handleAutocompleteInput={handleAutocompleteInput}
            />

            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 40 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <>
                        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>
                            Deboarding Stage Name
                        </Typography>
                    </>
                </Box>
                <FormControl fullWidth size="small" error={!getReviewStages?.results?.length}>
                    <Select value={selectedOption} size="small" disabled={getReviewStages?.results?.length === 0} fullWidth onChange={handleOptionChange} displayEmpty>
                        {getReviewStatus === STATUS.SUCESS && getReviewStages && getReviewStages.results && getReviewStages.results.map((option: any) =>
                            <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                        )}
                    </Select>
                    {!getReviewStages?.results?.length && (
                        <FormHelperText>Create stages first before deboarding users.</FormHelperText>
                    )}
                </FormControl>
            </div>

            <Box sx={{ marginTop: "auto", paddingTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                <AddButton disabled={selectedExperts.length === 0 || updateStatus === STATUS.PENDING} startIcon={updateStatus === STATUS.PENDING ? <CircularProgress size={15} /> : ''} onClick={handleAddAllClick} fullWidth>
                    Deboard All
                </AddButton>
            </Box>
        </Box>
    );
};

export default DeboardExpertDrawer;
